import { useEffect, useState } from 'react'
import { X, Sparkles, ChevronDown, ChevronRight } from 'lucide-react';
import { tokens } from '../styles/design-tokens'
import { api } from '../shared/api/client'

interface Commit {
  hash: string
  subject: string
  body: string
  date: string
  raw_subject: string
}

interface ChangelogData {
  version: string
  commits: Commit[]
}

interface WhatsNewModalProps {
  isOpen: boolean
  onClose: () => void
}

const LS_KEY = 'pim_last_seen_commit'

export function getLastSeenCommit(): string | null {
  return localStorage.getItem(LS_KEY)
}

export function markCommitSeen(hash: string) {
  localStorage.setItem(LS_KEY, hash)
}

export async function fetchChangelog(): Promise<ChangelogData | null> {
  try {
    const res = await api.get('/system/changelog')
    return res.data?.data || res.data || null
  } catch {
    return null
  }
}

export default function WhatsNewModal({ isOpen, onClose }: WhatsNewModalProps) {
  const [changelog, setChangelog] = useState<ChangelogData | null>(null)
  const [showHistory, setShowHistory] = useState(false)
  const [expandedHash, setExpandedHash] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen) return
    fetchChangelog().then(setChangelog)
  }, [isOpen])

  if (!isOpen || !changelog) return null

  const latest = changelog.commits[0] || null
  const previous = changelog.commits.slice(1)

  const handleClose = () => {
    if (latest) markCommitSeen(latest.hash)
    onClose()
  }

  const toggleExpand = (hash: string) => {
    setExpandedHash(prev => prev === hash ? null : hash)
  }

  return (
    <>
      <div className={tokens.modalBackdrop} onClick={handleClose} />
      <div className={tokens.modalContainer}>
        <div
          className={`${tokens.modalContent} ${tokens.modalMd}`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className={`${tokens.modalHeader} flex items-center justify-between`}>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-indigo-100 dark:bg-indigo-950 flex items-center justify-center">
                <Sparkles className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-zinc-100">Что нового</h2>
                <p className="text-sm text-gray-500 dark:text-zinc-400">Версия {changelog.version}</p>
              </div>
            </div>
            <button onClick={handleClose} className="text-gray-400 dark:text-zinc-500 hover:text-gray-600 dark:hover:text-zinc-300 transition-colors">
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Body */}
          <div className={`${tokens.modalBody} max-h-[60vh]`}>
            {!latest ? (
              <p className="text-gray-500 dark:text-zinc-400 text-sm text-center py-8">Нет записей об изменениях</p>
            ) : (
              <div className="space-y-4">
                {/* Latest commit */}
                <div className="bg-indigo-50 dark:bg-indigo-950 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-medium text-indigo-600 dark:text-indigo-400 bg-indigo-100 dark:bg-indigo-900 px-2 py-0.5 rounded">
                      Последнее
                    </span>
                    <span className="text-xs text-gray-400 dark:text-zinc-500">{formatDate(latest.date)}</span>
                    <span className="text-xs text-gray-400 dark:text-zinc-500 font-mono">{latest.hash}</span>
                  </div>
                  <p className="text-sm font-medium text-gray-900 dark:text-zinc-100 leading-relaxed">
                    {capitalizeFirst(latest.subject)}
                  </p>
                  {latest.body && (
                    <p className="text-sm text-gray-600 dark:text-zinc-300 mt-2 whitespace-pre-line leading-relaxed">
                      {latest.body}
                    </p>
                  )}
                </div>

                {/* Previous changes accordion */}
                {previous.length > 0 && (
                  <div>
                    <button
                      onClick={() => setShowHistory(!showHistory)}
                      className="flex items-center gap-2 text-sm text-gray-500 dark:text-zinc-400 hover:text-gray-700 dark:hover:text-zinc-200 transition-colors w-full"
                    >
                      {showHistory ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                      <span>Предыдущие изменения ({previous.length})</span>
                    </button>

                    {showHistory && (
                      <div className="mt-3 space-y-1">
                        {previous.map((commit) => (
                          <div key={commit.hash} className="border border-gray-100 dark:border-zinc-800 rounded-lg">
                            <button
                              onClick={() => toggleExpand(commit.hash)}
                              className="w-full flex items-start gap-3 p-3 text-left hover:bg-gray-50 dark:hover:bg-zinc-800/60 transition-colors rounded-lg"
                            >
                              {expandedHash === commit.hash ? (
                                <ChevronDown className="h-4 w-4 mt-0.5 text-gray-400 dark:text-zinc-500 flex-shrink-0" />
                              ) : (
                                <ChevronRight className="h-4 w-4 mt-0.5 text-gray-400 dark:text-zinc-500 flex-shrink-0" />
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="text-sm text-gray-700 dark:text-zinc-200">
                                  {capitalizeFirst(commit.subject)}
                                </p>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className="text-xs text-gray-400 dark:text-zinc-500">{formatDate(commit.date)}</span>
                                  <span className="text-xs text-gray-400 dark:text-zinc-500 font-mono">{commit.hash}</span>
                                </div>
                              </div>
                            </button>
                            {expandedHash === commit.hash && (
                              <div className="px-10 pb-3">
                                <p className="text-sm text-gray-600 dark:text-zinc-300 whitespace-pre-line leading-relaxed">
                                  {commit.body?.trim()
                                    ? commit.body
                                    : commit.raw_subject
                                      ? `Оригинал: ${commit.raw_subject}`
                                      : 'Нет дополнительного описания'}
                                </p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className={tokens.modalFooter}>
            <button
              onClick={handleClose}
              className={`${tokens.btnBase} ${tokens.btnPrimary}`}
            >
              Понятно
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

function formatDate(dateStr: string): string {
  try {
    const [y, m, d] = dateStr.split('-').map(Number)
    return new Date(y, m - 1, d).toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
  } catch {
    return dateStr
  }
}

function capitalizeFirst(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}
